import { useMemo } from "react";

import { BTText } from "@btnet/core-components";
import { useEntitySnapshotsByEntityTypeAndEntityExternalIdGetV2 } from "@btnet/services-btnet/handlers/entity-snapshots/entity-snapshots";
import type { EntitySnapshotResponse } from "@btnet/services-btnet/handlers/models/entitySnapshotResponse";
import { EntityType } from "@btnet/services-btnet/handlers/models/entityType";

import { BdsIconX } from "@buildertrend/components";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { SnapshotVersionHistory } from "commonComponents/entity/snapshotVersionHistory/SnapshotVersionHistory";
import { type ISnapshotVersionItem } from "commonComponents/entity/snapshotVersionHistory/SnapshotVersionHistory.types";

import "./SpecificationVersionHistory.less";

function mapToVersionItem(
    item: EntitySnapshotResponse,
    index: number,
    total: number,
): ISnapshotVersionItem {
    return {
        id: item.snapshotExternalId,
        versionNumber: total - index,
        date: new Date(item.timestamp ?? 0),
        user:
            item.performingUserId != null
                ? {
                      globalUserId: item.performingUserId,
                      displayName: item.performingUsername ?? "",
                  }
                : null,
        description: item.snapshotDescription ?? null,
    };
}

interface ISpecificationVersionHistoryProps {
    specificationExternalId: string;
    onVersionSelected: (snapshotExternalId: string | null) => void;
    onClose: () => void;
    selectedSnapshotId?: string | null;
}

export function SpecificationVersionHistory({
    specificationExternalId,
    onVersionSelected,
    onClose,
    selectedSnapshotId,
}: Readonly<ISpecificationVersionHistoryProps>) {
    const {
        data: response,
        isLoading,
        isError,
    } = useEntitySnapshotsByEntityTypeAndEntityExternalIdGetV2(
        EntityType.Specification,
        specificationExternalId,
    );

    const handleVersionSelected = (version: ISnapshotVersionItem) => {
        const snapshotId = typeof version.id === "string" ? version.id : null;
        onVersionSelected(snapshotId);
    };

    const mappedVersions = useMemo(() => {
        const versions = response?.data ?? [];
        return versions.map((v, i) => mapToVersionItem(v, i, versions.length));
    }, [response?.data]);

    return (
        <>
            <div
                className="flex flex-row specification-version-history-header px-4 py-2 items-center"
                data-testid="version-history-header"
            >
                <BTText size="distinct-lg" text="Version history" />
                <BTButton
                    onClick={onClose}
                    data-testid="version-history-close-button"
                    icon={<BdsIconX />}
                    type="tertiary"
                    aria-label="Close version history"
                    className="specification-version-history-close"
                />
            </div>
            <SnapshotVersionHistory
                versions={mappedVersions}
                isLoading={isLoading}
                isError={isError}
                emptyMessage="No published versions yet. Publish to create the first version."
                selectedId={selectedSnapshotId}
                onVersionSelected={handleVersionSelected}
                testId="specificationVersionHistory"
            />
        </>
    );
}
